import { useEffect, useState } from 'react'

import { LoadingLottie } from '@/components/loading/LoadingLottie'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import RenderIf from '@/components/ui/render-if'
import { Section } from '@/components/ui/section'
import { Text } from '@/components/ui/text'
import { ERROR_CODES } from '@/constants/error-code'
import { ILinkWalletWithReferralCodeParams, useMutationLinkWalletWithReferralCode, useQueryReferrerInfo } from '@/data'
import { useToast } from '@/hooks/use-toast'
import {
  useAlreadyOpenConfirmRegister,
  useOpenConfirmRegister,
  usePersistReferralActions,
  usePersistReferralCode,
  useReferralActions,
  useReferralWallet,
} from '@/store/referral'
import { truncateAddress } from '@/utils'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useReferralSignature } from './useReferralSignature'

import { ConfirmRegisterReferral } from '../confirm-register-referral'

export const RegisterReferral = () => {
  const { t } = useTranslation()
  const { code } = useParams()
  const persistReferralCode = usePersistReferralCode()
  const referralCode = code || persistReferralCode
  const referralWallet = useReferralWallet()
  const openConfirmRegister = useOpenConfirmRegister()
  const alreadyOpenConfirmRegister = useAlreadyOpenConfirmRegister()
  const { setOpenConfirmRegister } = useReferralActions()
  const { setReferralCode } = usePersistReferralActions()
  const toast = useToast()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      referralCode: referralCode,
    },
  })
  const { data: referrerInfo, isLoading: isCheckingHasBeenRef } = useQueryReferrerInfo({
    walletAddress: referralWallet?.address || '',
  })
  const { mutateAsync: linkWalletWithReferralCode } = useMutationLinkWalletWithReferralCode()
  const [isPending, setIsPending] = useState(false)
  const referralCodeValue = watch('referralCode')
  const getSignature = useReferralSignature(referralWallet, t)

  useEffect(() => {
    // Store referral code in local storage to use when user go back to the page
    if (code) {
      setReferralCode(code)
    }
  }, [code])

  useEffect(() => {
    if (!referrerInfo?.referrer) return
    setOpenConfirmRegister(false)
  }, [referrerInfo?.referrer])

  useEffect(() => {
    if (code && referralWallet && !alreadyOpenConfirmRegister) {
      setOpenConfirmRegister(true)
    }
  }, [code, referralWallet, alreadyOpenConfirmRegister])

  const handleSubmitReferralCode = async (referralCode?: string) => {
    try {
      if (isPending) return
      setError('referralCode', { message: '' })
      setIsPending(true)
      const code = referralCode?.toUpperCase() || ''
      const { signature, publicKey, chainType, timestamp } = await getSignature(code)
      const payload: ILinkWalletWithReferralCodeParams = {
        code,
        walletAddress: referralWallet?.address || '',
        publicKey: publicKey || '',
        chainType: chainType,
        timestamp,
        signature,
      }
      setOpenConfirmRegister(false)
      await linkWalletWithReferralCode(payload)
    } catch (error: any) {
      if (error?.errorCode === ERROR_CODES.NOTFOUND) {
        setError('referralCode', { message: t('error.INVALID_REFERRAL_CODE') })
      }
      toast.toast({
        title: t('error.ERROR'),
        description: error.message,
        variant: 'destructive',
      })
    } finally {
      setIsPending(false)
    }
  }
  if (isCheckingHasBeenRef)
    return (
      <Section ring={false} className='space-y-5 w-full md:w-[284px] flex flex-col items-center justify-center'>
        <LoadingLottie className='size-6' />
      </Section>
    )
  if (referrerInfo)
    return (
      <Section ring={false} className='space-y-4 w-full md:w-[284px] flex flex-col items-center justify-center'>
        <Text>{t('title.YOU_WERE_REFERRED_BY')}</Text>
        <Text as='p' size={18}>
          {truncateAddress(referrerInfo?.referrer)}
        </Text>
        <Text as='p' color='white-48'>
          {t('title.CODE')}: <Text as='span'>{referrerInfo?.accessCode}</Text>
        </Text>
      </Section>
    )
  return (
    <Section ring={false} className='space-y-5 w-full md:w-[284px]'>
      <Text as='p'>{t('title.ENTER_REFERRAL_CODE')}</Text>
      <Text as='p' color='white-48'>
        {t('title.PLEASE_ENTER_REFERRAL_CODE')}
      </Text>
      <form className='space-y-5' onSubmit={handleSubmit((values) => handleSubmitReferralCode(values.referralCode))}>
        <div>
          <Input
            size='lg'
            placeholder={t('title.ENTER_REFERRAL_CODE_PLACEHOLDER')}
            className='uppercase placeholder:capitalize text-size-16'
            autoComplete='off'
            autoCorrect='off'
            {...register('referralCode')}
            onBlur={(e) => {
              setReferralCode(e.target.value)
            }}
          />
          <RenderIf condition={!!errors.referralCode}>
            <Text as='p' color='red' size={12} className='mt-1'>
              {errors.referralCode?.message}
            </Text>
          </RenderIf>
        </div>
        <Button type='submit' className='w-full' disabled={!referralCodeValue} isLoading={isPending}>
          {t('button.CONFIRM_REFERRAL')}
        </Button>
      </form>
      {openConfirmRegister && (
        <ConfirmRegisterReferral
          referralCode={referralCode || ''}
          onSubmit={handleSubmitReferralCode}
          isLoading={isPending}
        />
      )}
    </Section>
  )
}
